import React from 'react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { APP_NAME } from '../../config/branding';
import { useTheme, ThemeMode } from '../../contexts/ThemeContext';

const AppearanceSettings: React.FC = () => {
  const { themeMode, setThemeMode, effectiveTheme } = useTheme();

  const themeOptions: { mode: ThemeMode; label: string; description: string; icon: React.ReactNode }[] = [
    {
      mode: 'light',
      label: 'Light',
      description: 'Always use light mode',
      icon: <Sun className="w-6 h-6" />,
    },
    {
      mode: 'dark',
      label: 'Dark',
      description: 'Always use dark mode',
      icon: <Moon className="w-6 h-6" />,
    },
    {
      mode: 'system',
      label: 'System',
      description: 'Matches your device settings',
      icon: <Monitor className="w-6 h-6" />,
    },
  ];

  return (
    <div className="max-w-3xl mx-auto p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-2">
          Appearance
        </h1>
        <p className="text-gray-600 dark:text-dark-text-secondary">
          Customize how {APP_NAME} looks on your device
        </p>
      </div>

      {/* Theme Selection */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-sm border border-gray-200 dark:border-dark-border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2">
          Theme
        </h2>
        <p className="text-sm text-gray-500 dark:text-dark-text-muted mb-6">
          Choose your preferred theme for the interface
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {themeOptions.map((option) => {
            const isSelected = themeMode === option.mode;
            return (
              <button
                key={option.mode}
                onClick={() => setThemeMode(option.mode)}
                className={`
                  relative flex flex-col items-center p-6 rounded-xl border-2 transition-all
                  ${isSelected 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-dark-border-hover bg-white dark:bg-dark-bg-tertiary'
                  }
                `}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}

                {/* Theme preview */}
                <div className={`
                  w-16 h-12 rounded-lg mb-4 overflow-hidden shadow-sm
                  ${option.mode === 'light' && 'bg-white border border-gray-200'}
                  ${option.mode === 'dark' && 'bg-gray-900 border border-gray-700'}
                  ${option.mode === 'system' && 'bg-gradient-to-r from-white to-gray-900 border border-gray-300'}
                `}>
                  <div className={`
                    h-full flex flex-col p-1.5
                    ${option.mode === 'light' && 'text-gray-800'}
                    ${option.mode === 'dark' && 'text-gray-200'}
                    ${option.mode === 'system' && ''}
                  `}>
                    {option.mode === 'light' && (
                      <>
                        <div className="w-full h-1.5 bg-gray-200 rounded mb-1"></div>
                        <div className="w-3/4 h-1 bg-gray-300 rounded mb-0.5"></div>
                        <div className="w-1/2 h-1 bg-gray-300 rounded"></div>
                      </>
                    )}
                    {option.mode === 'dark' && (
                      <>
                        <div className="w-full h-1.5 bg-gray-700 rounded mb-1"></div>
                        <div className="w-3/4 h-1 bg-gray-600 rounded mb-0.5"></div>
                        <div className="w-1/2 h-1 bg-gray-600 rounded"></div>
                      </>
                    )}
                    {option.mode === 'system' && (
                      <div className="flex h-full">
                        <div className="flex-1 flex flex-col p-0.5">
                          <div className="w-full h-1 bg-gray-200 rounded mb-0.5"></div>
                          <div className="w-3/4 h-0.5 bg-gray-300 rounded"></div>
                        </div>
                        <div className="flex-1 flex flex-col p-0.5">
                          <div className="w-full h-1 bg-gray-700 rounded mb-0.5"></div>
                          <div className="w-3/4 h-0.5 bg-gray-600 rounded"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Icon */}
                <div className={`
                  mb-2
                  ${isSelected 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-500 dark:text-dark-text-secondary'
                  }
                `}>
                  {option.icon}
                </div>

                {/* Label */}
                <span className={`
                  font-medium mb-1
                  ${isSelected 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-900 dark:text-dark-text-primary'
                  }
                `}>
                  {option.label}
                </span>

                {/* Description */}
                <span className="text-xs text-gray-500 dark:text-dark-text-muted text-center">
                  {option.description}
                </span>
              </button>
            );
          })}
        </div>

        {/* Current status */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-border">
          <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
            <span className="font-medium">Current theme:</span>{' '}
            <span className="capitalize">{effectiveTheme}</span>
            {themeMode === 'system' && (
              <span className="text-gray-400 dark:text-dark-text-muted ml-1">
                (following system preference)
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AppearanceSettings;

